import CloseIcon from '@mui/icons-material/Close'
import SendRoundedIcon from '@mui/icons-material/SendRounded'
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  IconButton,
  InputBase,
  Stack,
  Typography,
  useMediaQuery
} from '@mui/material'
import { KeyboardEvent, useEffect, useMemo, useRef, useState } from 'react'
import { MidasConfig } from 'api/midas'
import theme from 'utils/theme'
import MessageBubble, { MidasAvatar } from './MessageBubble'
import MarkdownLite from './MarkdownLite'
import { useMidasChat } from './useMidasChat'

export const textButtonSx = {
  height: 'auto',
  minWidth: 0,
  fontSize: '0.875rem',
  textTransform: 'none',
  '&.Mui-disabled': { bgcolor: 'transparent', color: 'text.disabled' }
} as const

const HANDED_OVER_BANNER =
  "You're chatting with the Goldie team — we'll reply here and by email"

export const TALK_TO_A_PERSON = 'Talk to a person'
const TITLE_ID = 'midas-panel-title'

interface MidasPanelProps {
  open: boolean
  onClose: () => void
  config: MidasConfig
}

export default function MidasPanel({
  open,
  onClose,
  config
}: Readonly<MidasPanelProps>) {
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'))
  const chat = useMidasChat(open)
  const [draft, setDraft] = useState('')
  const listRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLTextAreaElement>(null)

  const hasCustomerMessage = chat.messages.some(
    m => m.author_type === 'customer'
  )
  const ready = !!chat.conversation && !chat.error && !chat.closed
  // Before the first message the quick replies usually carry this action.
  const showHandoverAction =
    hasCustomerMessage || !config.quick_replies.includes(TALK_TO_A_PERSON)
  const lastMessage = chat.messages[chat.messages.length - 1]

  // Keep the newest message in view as it grows.
  useEffect(() => {
    const list = listRef.current
    if (list) list.scrollTop = list.scrollHeight
  }, [chat.messages, open])

  // The composer is disabled while Midas replies; hand focus back after.
  useEffect(() => {
    if (open && ready && !chat.replying) inputRef.current?.focus()
  }, [open, ready, chat.replying])

  // Announce finished replies once, not every streamed chunk.
  const announcement = useMemo(() => {
    const finished = [...chat.messages]
      .reverse()
      .find(
        m =>
          (m.author_type === 'midas' || m.author_type === 'agent') &&
          m.status === 'complete'
      )
    if (!finished) return ''
    const who = finished.author_type === 'agent' ? 'Goldie team' : 'Midas'
    return `${who}: ${finished.body}`
  }, [chat.messages])

  const submit = async (text: string) => {
    if (chat.replying || !ready || !text.trim()) return
    if (await chat.send(text)) setDraft('')
  }

  const onKeyDown = (event: KeyboardEvent<HTMLTextAreaElement>) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault()
      submit(draft)
    }
  }

  const onQuickReply = (reply: string) => {
    if (reply === TALK_TO_A_PERSON) chat.handover()
    else submit(reply)
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullScreen={fullScreen}
      aria-labelledby={TITLE_ID}
      TransitionProps={{ onEntered: () => inputRef.current?.focus() }}
      slotProps={{
        backdrop: { sx: { bgcolor: 'rgba(33, 30, 22, 0.12)' } }
      }}
      PaperProps={{
        sx: fullScreen
          ? { display: 'flex', flexDirection: 'column' }
          : {
              position: 'fixed',
              right: 16,
              bottom: 16,
              m: 0,
              width: 400,
              maxWidth: 'calc(100vw - 32px)',
              height: 'min(640px, calc(100vh - 32px))',
              maxHeight: 'none',
              borderRadius: 3,
              display: 'flex',
              flexDirection: 'column'
            }
      }}
    >
      <Stack
        direction='row'
        alignItems='center'
        gap={1.5}
        sx={{ px: 2, py: 1.5, borderBottom: '1px solid #EEE9E0' }}
      >
        <MidasAvatar size={40} />
        <Box flex={1} minWidth={0}>
          <Typography id={TITLE_ID} component='h2' fontWeight={700}>
            Midas
          </Typography>
          <Typography variant='caption' color='text.secondary'>
            Goldie&apos;s AI assistant
          </Typography>
        </Box>
        <IconButton aria-label='Close chat' onClick={onClose}>
          <CloseIcon />
        </IconButton>
      </Stack>

      {chat.handedOver && (
        <Alert severity='info' icon={false} sx={{ borderRadius: 0 }}>
          {config.support_hours
            ? `${HANDED_OVER_BANNER}. Support hours: ${config.support_hours}`
            : HANDED_OVER_BANNER}
        </Alert>
      )}

      <Stack
        ref={listRef}
        role='log'
        aria-live='off'
        aria-label='Conversation with Midas'
        gap={1.5}
        sx={{ flex: 1, overflowY: 'auto', px: 2, py: 2 }}
      >
        <Stack direction='row' gap={1} alignItems='flex-start' maxWidth='88%'>
          <MidasAvatar />
          <Box minWidth={0}>
            <Typography variant='caption' color='text.secondary' component='p'>
              Midas
            </Typography>
            <Box
              sx={{
                bgcolor: '#F3F0EA',
                borderRadius: '4px 16px 16px 16px',
                px: 1.5,
                py: 1,
                fontSize: '0.9375rem',
                lineHeight: 1.45
              }}
            >
              <MarkdownLite text={config.welcome_message} />
            </Box>
          </Box>
        </Stack>

        {ready && !hasCustomerMessage && !chat.handedOver && (
          <Stack direction='row' flexWrap='wrap' gap={1} pl={5}>
            {config.quick_replies.map(reply => (
              <Chip
                key={reply}
                label={reply}
                variant='outlined'
                color='primary'
                onClick={() => onQuickReply(reply)}
                disabled={chat.replying}
              />
            ))}
          </Stack>
        )}

        {chat.messages.map(message => (
          <MessageBubble
            key={message.id}
            message={message}
            onFeedback={chat.giveFeedback}
            onRetry={
              message === lastMessage && chat.lastCustomerBody
                ? () => submit(chat.lastCustomerBody ?? '')
                : undefined
            }
          />
        ))}

        {chat.loading && (
          <Typography variant='caption' color='text.secondary' px={5}>
            Connecting…
          </Typography>
        )}

        {chat.error && (
          <Alert
            severity='error'
            action={
              <Button
                variant='text'
                size='small'
                onClick={chat.retry}
                sx={textButtonSx}
              >
                Retry
              </Button>
            }
          >
            Midas couldn&apos;t connect just now.
          </Alert>
        )}

        {chat.closed && (
          <Alert
            severity='info'
            action={
              <Button
                variant='text'
                size='small'
                onClick={chat.startNewChat}
                sx={textButtonSx}
              >
                Start a new chat
              </Button>
            }
          >
            This conversation is closed
          </Alert>
        )}

        {chat.sendError && (
          <Alert severity='error' role='alert'>
            {chat.sendError}
          </Alert>
        )}
      </Stack>

      <Box
        aria-live='polite'
        sx={{
          position: 'absolute',
          width: '1px',
          height: '1px',
          overflow: 'hidden',
          clip: 'rect(0 0 0 0)'
        }}
      >
        {announcement}
      </Box>

      <Box sx={{ borderTop: '1px solid #EEE9E0', px: 2, pt: 1, pb: 1.5 }}>
        {ready && !chat.handedOver && showHandoverAction && (
          <Button
            variant='text'
            size='small'
            onClick={chat.handover}
            disabled={chat.replying}
            sx={{ ...textButtonSx, mb: 0.5, px: 0.5 }}
          >
            {TALK_TO_A_PERSON}
          </Button>
        )}
        <Stack direction='row' alignItems='flex-end' gap={1}>
          <InputBase
            inputRef={inputRef}
            value={draft}
            onChange={event => setDraft(event.target.value)}
            onKeyDown={onKeyDown}
            placeholder={
              chat.handedOver ? 'Message the Goldie team' : 'Ask Midas…'
            }
            inputProps={{ 'aria-label': 'Message' }}
            disabled={!ready || chat.replying}
            multiline
            maxRows={5}
            sx={{
              flex: 1,
              bgcolor: '#F7F5F0',
              borderRadius: 5,
              px: 2,
              py: 1,
              fontSize: '0.9375rem'
            }}
          />
          <IconButton
            aria-label='Send'
            color='primary'
            onClick={() => submit(draft)}
            disabled={!ready || chat.replying || !draft.trim()}
          >
            <SendRoundedIcon />
          </IconButton>
        </Stack>
      </Box>
    </Dialog>
  )
}
